/**
 * What the reader sent WHILE Weave was working, and where each message stands (`laneV2/followUps.ts`).
 *
 * It sits in the composer dock, where the message was typed: the transcript is built from the server's
 * turns (R5), which do not carry these yet, so a row here is the only place the reader sees that the
 * message was taken, and what became of it. A message the turn did not act on is sent as the next turn
 * by the store and leaves this strip then.
 */
import { Check, Clock, Loader2, RotateCcw, TriangleAlert, X } from "lucide-react";
import { followUpCaption, WITHOUT_FILE_COPY, type FollowUp, type FollowUpStatus } from "@/services/multi-agent/laneV2/followUps";

const ICON: Record<FollowUpStatus, typeof Check> = {
  sending: Loader2,
  waiting: Clock,
  included: Check,
  unconfirmed: TriangleAlert,
  undelivered: TriangleAlert,
  failed: TriangleAlert,
};

/** Only a message Weave did not read can be sent again; a waiting one is still on its way. */
const RESENDABLE: ReadonlySet<FollowUpStatus> = new Set(["failed", "unconfirmed", "undelivered"]);

export interface LaneV2FollowUpStripProps {
  followUps: readonly FollowUp[];
  /** Send this text again as a normal message, and drop the row. */
  onResend: (followUp: FollowUp) => void;
  onDismiss: (followUp: FollowUp) => void;
}

export function LaneV2FollowUpStrip({ followUps, onResend, onDismiss }: LaneV2FollowUpStripProps) {
  if (!followUps.length) return null;
  return (
    <ul role="status" aria-label="Messages sent while Weave was working" className="flex flex-col gap-1.5" data-lane-v2-follow-ups="">
      {followUps.map((f) => {
        const Icon = ICON[f.status];
        const settled = f.status !== "sending" && f.status !== "waiting";
        return (
          <li
            key={f.localId}
            data-follow-up-status={f.status}
            className="flex items-start gap-2 rounded-md border border-border bg-muted/30 px-2.5 py-1.5 text-[12px]"
          >
            <Icon
              aria-hidden
              className={`mt-0.5 h-3.5 w-3.5 shrink-0 ${f.status === "sending" ? "animate-spin" : ""} ${RESENDABLE.has(f.status) ? "text-amber-600" : "text-muted-foreground"}`}
            />
            <div className="min-w-0 flex-1">
              <p className="whitespace-pre-wrap break-words text-foreground">{f.text}</p>
              <p className="text-muted-foreground">{followUpCaption(f)}</p>
              {f.withoutFile && (
                <p className="text-muted-foreground" data-follow-up-without-file="">
                  {WITHOUT_FILE_COPY}
                </p>
              )}
            </div>
            {RESENDABLE.has(f.status) && (
              <button
                type="button"
                onClick={() => onResend(f)}
                className="inline-flex shrink-0 items-center gap-1 rounded px-1.5 py-0.5 text-[11px] text-foreground hover:bg-muted"
              >
                <RotateCcw aria-hidden className="h-3 w-3" />
                Send again
              </button>
            )}
            {settled && (
              <button
                type="button"
                onClick={() => onDismiss(f)}
                aria-label="Dismiss"
                className="shrink-0 rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
              >
                <X aria-hidden className="h-3 w-3" />
              </button>
            )}
          </li>
        );
      })}
    </ul>
  );
}
